import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import {
  Search,
  Upload,
  Download,
  Copy,
  Eye,
  ArrowLeft,
  Plus,
  Database,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  useUpdateContentMutation,
  getContents,
  useContentsQuery,
  useDeleteContentMutation,
  useUploadJupyterFileMutation
} from "../api/jupyterManagementApi";
import { toast } from "sonner";
import { useJupyterWorkspace } from "../contexts/JupyterWorkspaceContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../App";
import paths from "../routes";

type DatasetType = "Project File" | "Template" | "User Created";

interface Dataset {
  id: string;
  name: string;
  description?: string;
  author?: string;
  lastModified?: string;
  size?: string;
  version?: string;
  type?: DatasetType;
  tags?: string[];
  format?: string;
  path?: string;
}

function getTypeColor(type?: DatasetType) {
  switch (type) {
    case "Project File":
      return "bg-bits-blue/10 text-bits-blue border-bits-blue";
    case "Template":
      return "bg-bits-gold/10 text-bits-gold border-bits-gold";
    case "User Created":
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

const generateUniqueFilename = (
  originalName: string,
  existingNames: Set<string>
): string => {
  if (!existingNames.has(originalName.toLowerCase())) {
    return originalName;
  }

  const lastDotIndex = originalName.lastIndexOf(".");
  const baseName =
    lastDotIndex !== -1
      ? originalName.substring(0, lastDotIndex)
      : originalName;
  const extension =
    lastDotIndex !== -1 ? originalName.substring(lastDotIndex) : "";
  const suffixMatch = baseName.match(/^(.*?)\((\d+)\)$/);
  let nameWithoutSuffix = suffixMatch ? suffixMatch[1] : baseName;
  let counter = suffixMatch ? parseInt(suffixMatch[2], 10) : 1;
  let candidate = `${nameWithoutSuffix}(${counter})${extension}`;
  while (existingNames.has(candidate.toLowerCase())) {
    counter++;
    candidate = `${nameWithoutSuffix}(${counter})${extension}`;
  }
  return candidate;
};

const ALLOWED_EXTS = new Set(["csv", "txt", "md", "xls", "xlsx"]);

function getExt(name?: string): string {
  if (!name) return "";
  const i = name.lastIndexOf(".");
  return i === -1 ? "" : name.slice(i + 1).toLowerCase();
}

function isAllowedName(name?: string) {
  if (!name) return false;
  return ALLOWED_EXTS.has(getExt(name));
}

function inferMimeFromName(name: string) {
  const ext = getExt(name);
  switch (ext) {
    case "csv":
      return "text/csv";
    case "txt":
      return "text/plain";
    case "md":
      return "text/markdown";
    case "xls":
      return "application/vnd.ms-excel";
    case "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    default:
      return "text/plain";
  }
}

export default function DatasetsPage() {
  const navigate = useNavigate();
  const { workspace } = useJupyterWorkspace();
  const { user } = useAuth();
  const updateContentMutation = useUpdateContentMutation();
  const deleteContentMutation = useDeleteContentMutation();
  const uploadLTIFileMutation = useUploadJupyterFileMutation();
  const queryClient = useQueryClient();
    const {
    data: contentsData,
    isLoading,
    error,
  } = useContentsQuery(workspace?.folderPath || "", workspace?.workspaceType === "local", !!workspace?.folderPath);
  const [searchTerm, setSearchTerm] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { projectId } = useParams();

  const datasets = useMemo(() => {
    if (!contentsData?.content) return [];
    const files = contentsData.content;
    return files
      ?.filter((f: any) => f.type === "file" && isAllowedName(f?.name))
      .map((f: any) => ({
        id: f.path ?? f.name,
        name: f.name,
        description: f.description ?? "",
        author: f.author ?? "",
        lastModified: f.last_modified ?? f.lastModified ?? "",
        size: f.size ?? "",
        version: f.version ?? "",
        type: (f.type as DatasetType) ?? "User Created",
        tags: f.tags ?? [],
        format: inferMimeFromName(f.name),
        path: f.path,
      }));
  }, [contentsData]);

  useEffect(() => {
    if (error) {
      console.error("Failed to fetch datasets:", error);
      toast.error("Failed to load datasets");
    }
  }, [error]);

  const filteredDatasets = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const base = datasets?.filter((d) => isAllowedName(d?.name));
    if (!q) return base;
    return base.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        (d.description ?? "").toLowerCase().includes(q) ||
        (d.tags ?? []).some((t) => t.toLowerCase().includes(q))
    );
  }, [datasets, searchTerm]);

  const buildTargetPath = (filename: string) => {
    const basePath = workspace?.folderPath;
    return `${basePath}/${filename}`;
  };
  const MAX_FILE_SIZE = 50 * 1024 * 1024;
const ALLOWED_EXTS = ["csv", "txt", "md", "xls", "xlsx"]; // matches page guidance

const handleUpload = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.multiple = true;
  input.accept = ".csv,.txt,.md,.xls,.xlsx";
  input.onchange = async (e: any) => {
    const list = (e.target as HTMLInputElement).files;
    const selected = list ? Array.from(list) : [];
    if (!selected.length || !projectId) return;

    if (selected.length > 20) {
      toast.error("You can upload at most 20 files at a time.");
      return;
    }

    const tooLarge = selected.filter(f => f.size > MAX_FILE_SIZE);
    if (tooLarge.length) {
      toast.error(
        `Some files exceed the 50MB limit: ${tooLarge
          .map(f => `${f.name} (${(f.size / (1024 * 1024)).toFixed(1)}MB)`)
          .join(", ")}.`
      );
      return;
    }

    const invalid = selected.filter(f => {
      const ext = f.name.split(".").pop()?.toLowerCase();
      return !ext || !ALLOWED_EXTS.includes(ext);
    });
    if (invalid.length) {
      toast.error(
        `Unsupported file types: ${invalid.map(f => f.name).join(", ")}.`
      );
      return;
    }

    const existingNames = new Set((datasets ?? []).map(d => d.name.toLowerCase()));
    const dupExisting = selected.filter(f => existingNames.has(f.name.toLowerCase()));
    if (dupExisting.length) {
      toast.error(
        `Already exists: ${dupExisting.map(f => f.name).join(", ")}. Please rename.`
      );
      return;
    }

    const seen = new Map<string, number>();
    const dupInSelection = selected.filter(f => {
      const k = f.name.toLowerCase();
      seen.set(k, (seen.get(k) ?? 0) + 1);
      return (seen.get(k) ?? 0) > 1;
    });
    if (dupInSelection.length) {
      const names = [...new Set(dupInSelection.map(f => f.name))];
      toast.error(`Duplicate names in selection: ${names.join(", ")}.`);
      return;
    }

    const loading = toast.loading(`Uploading ${selected.length} files...`);
    try {
      await uploadLTIFileMutation.mutateAsync({ files: selected, projectId, username: user?.id ? `user_${user.id}` : undefined });
      toast.dismiss(loading);
      toast.success(`Uploaded ${selected.length} files successfully.`);
      await queryClient.invalidateQueries({ queryKey: ["jupyter", "contents"] });
      await queryClient.invalidateQueries({ queryKey: ["files", projectId] });
    } catch (error: any) {
      toast.dismiss(loading);
      if (error?.response?.status === 413) {
        toast.error("File too large. Please use a file smaller than 50MB.");
      } else if (error?.response?.status === 415) {
        toast.error("Unsupported file type. Please try a different format.");
      } else if (error?.response?.data?.data?.message) {
        toast.error(`Upload failed: ${error.response.data.data.message}`);
      } else {
        toast.error(`Upload failed: ${error?.response?.data?.message ?? "An unknown error occurred."}`);
      }
    }
  };
  input.click();
};

  const handleDownload = async (dataset: Dataset) => {
    try {
      const filePath = (dataset as any).path ?? buildTargetPath(dataset.name);
      const fileModel = await getContents(filePath, workspace?.workspaceType === "local");
      const contentText =
        typeof fileModel?.content === "string" ? fileModel.content : "";
      if (!contentText) {
        toast.error("No content available to download.");
        return;
      }

      const blob = new Blob([contentText], {
        type: `${inferMimeFromName(dataset.name)};charset=utf-8;`,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", dataset.name);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Downloading ${dataset.name}`);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Download failed");
    }
  };

  const handleDuplicate = async (dataset: Dataset) => {
    const loadingToast = toast.loading(`Duplicating ${dataset.name}...`);
    try {
      const resolvePath = (name: string) => {
        const basePath = workspace?.folderPath || "";
        return `${basePath}/${name}`;
      };
      const sourcePath = (dataset as any).path ?? resolvePath(dataset.name);

      const sourceModel = await getContents(sourcePath, workspace?.workspaceType === "local");
      const sourceText =
        typeof sourceModel?.content === "string" ? sourceModel.content : "";
      if (!sourceText) {
        toast.error("No content available to duplicate.");
        return;
      }
      // Generate unique -copy name
      const existingNames = new Set(datasets?.map((d) => d.name.toLowerCase()));
      const parts = dataset.name.split(".");
      const ext = parts.length > 1 ? `.${parts.pop()}` : ".csv";
      const base = parts.join(".");
      const mkName = (idx: number | null) =>
        idx === null ? `${base}-copy${ext}` : `${base}-copy-${idx}${ext}`;

      let candidate = mkName(null);
      let idx = 1;
      while (existingNames.has(candidate.toLowerCase())) {
        candidate = mkName(idx++);
      }

      const targetPath = resolvePath(candidate);

      await updateContentMutation.mutateAsync({
        path: targetPath,
        content: sourceText,
        type: "file",
        format: "text",
      });
      await queryClient.invalidateQueries({
        queryKey: ["jupyter", "contents"],
      });
      await queryClient.invalidateQueries({ queryKey: ["files", projectId] });
      toast.success(`Duplicated ${dataset.name} → ${candidate}`);
    } catch (err: any) {
      console.error("Duplicate failed:", err);
      toast.error(err?.response?.data?.message || "Duplicate failed");
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleDelete = async (dataset: Dataset) => {
    if (deletingId) return;
    const isConfirmed = window.confirm(
      `Are you sure you want to delete "${dataset.name}"? This action cannot be undone.`
    );
    if (!isConfirmed) return;
    setDeletingId(dataset.id);
    try {
      const filePath = (dataset as any).path ?? buildTargetPath(dataset.name);
      await deleteContentMutation.mutateAsync(filePath);
      await queryClient.invalidateQueries({
        queryKey: ["jupyter", "contents"],
      });
      await queryClient.invalidateQueries({ queryKey: ["files", projectId] });
      toast.success(`Deleted ${dataset.name}`);
    } catch (err: any) {
      console.error("Delete failed:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleView = async (dataset: Dataset) => {
     await queryClient.refetchQueries({ 
      queryKey: ["jupyter", "contents"],
      type: "active" 
    });
    console.log("dataset to view", dataset);
    const freshData = queryClient.getQueryData(["jupyter", "contents"]);
    navigate(paths.notebook.replace(":projectId", projectId!), {
      state: { file: dataset },
    });
  };

  return (
    <div className="p-4 mt-4 space-y-2 min-h-screen bg-white">
      <div className="flex items-center">
        <Button
          variant="ghost"
          className="p-4 mt-2"
          onClick={() => {
            navigate(`${paths.notebook.replace(":projectId", projectId!)}`);
          }}
        >
          <ArrowLeft className="!h-6 !w-6 text-gray-700" />
        </Button>
        <h2 className="text-xl font-semibold flex gap-2 mt-2">Datasets</h2>
        <div className="ml-auto flex items-center gap-2">
          <Button onClick={handleUpload} variant="secondary" className="mt-4">
            <Upload className="h-4 w-4" />
            Upload dataset
          </Button>
        </div>
      </div>

      <p className="text-muted-foreground pr-2 pb-2">
        Manage datasets, upload new data files for data science projects
      </p>

      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search datasets..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="grid gap-3">
        {filteredDatasets?.map((d) => (
          <div key={d.id} className="border rounded-lg p-3 bg-background">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium">{d.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleView(d)}
                >
                  <Eye className="h-4 w-4" />
                  Open
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(d)}
                >
                  Download
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDuplicate(d)}
                >
                  Duplicate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(d)}
                  disabled={deletingId !== null}
                >
                  {deletingId === d.id ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-white" />
                  ) : (
                    <>Delete</>
                  )}
                </Button>
              </div>
            </div>

            <div className="mt-1 text-sm text-muted-foreground">
              {d.description ?? ""}
            </div>

            <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
              <span>📁 {d.size ?? "—"}kb</span>
            </div>

            <div className="mt-2 flex gap-2">
              {(d.tags ?? [])?.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        ))}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-bits-blue mb-4" />
            <p className="text-lg font-medium text-gray-900">
              Loading datasets...
            </p>
          </div>
        ) : filteredDatasets.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
            {searchTerm.trim() !== "" ? (
              <div className="space-y-2">
                <Database className="h-12 w-12 text-muted-foreground mx-auto" />
                <p className="text-lg font-medium text-gray-900">
                  No datasets match your search
                </p>
                <p className="text-sm text-muted-foreground">
                  Try adjusting your search term or upload a new dataset
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <Database className="h-16 w-16 text-muted-foreground mx-auto" />
                <div className="space-y-2">
                  <p className="text-lg font-medium text-gray-900">
                    No datasets available
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Upload a dataset to get started (CSV, TXT, or MD files)
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
